import { Component, HostBinding, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { RenderNotImplementedError } from '../render-flow.model';
import { RenderFlowService } from '../render-flow.service';
import { RENDER_PRICE_USD, RENDER_SUPPORT_EMAIL, RENDER_VIDEO_SPEC_PARTS } from '../render.data';
import { RenderStepBase } from './render-step-base';

// Stripe usually confirms in seconds: the page asks this often at first, and after this long says
// it is still waiting and offers "Check again" — while still asking, less often.
const QUICK_CHECK_MS = 3000;
const SLOW_AFTER_MS = 30000;
const SLOW_CHECK_MS = 10000;

// Between checkout and the render: the payment is not confirmed yet. Stripe's return lands here
// with ?session_id=… (RenderCheckoutReturnGuard).
// The checkout's own layout — the pack on both devices — with the card saying what happens
// (proposals 2026-09-30): the page checks by itself, a receipt line says what is being paid for,
// and only after 30 seconds come "Check again" and support. Confirmed, the flow moves on to the
// render by itself.
@Component({
  selector: 'app-render-confirming-step',
  templateUrl: './render-confirming-step.component.html',
})
export class RenderConfirmingStepComponent extends RenderStepBase implements OnInit, OnDestroy {
  @HostBinding('class') public readonly hostClass = 'render-step step-confirming';

  public readonly price = RENDER_PRICE_USD;
  public readonly videoSpec = RENDER_VIDEO_SPEC_PARTS;
  public checking = false;
  // The user's own "Check again" — the button says "Checking…" for that one, not for the page's.
  public checkingByHand = false;
  public error = '';
  public slow = false;
  public checks = 0;
  public lastCheck = 0;

  private sessionId = '';
  private readonly arrivedAt = Date.now();
  private timer?: number;
  private slowTimer?: number;
  private destroyed = false;

  constructor(route: ActivatedRoute, router: Router, flow: RenderFlowService) {
    super(route, router, flow);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.sessionId = this.route.snapshot.queryParamMap.get('session_id') || '';
    if (this.sessionId) {
      // Drop the session id from the URL so a reload does not re-run the confirmation.
      this.router.navigate([], { relativeTo: this.route, queryParams: { session_id: null }, queryParamsHandling: 'merge', replaceUrl: true });
    }
    this.slowTimer = window.setTimeout(() => (this.slow = true), SLOW_AFTER_MS);
    this.schedule(0);
  }

  override ngOnDestroy(): void {
    this.destroyed = true;
    window.clearTimeout(this.timer);
    window.clearTimeout(this.slowTimer);
    super.ngOnDestroy();
  }

  public get supportHref(): string {
    const subject = encodeURIComponent(`Payment not confirmed · ${this.job?.name || ''} · ${this.job?.modelId || ''}`);
    return `mailto:${RENDER_SUPPORT_EMAIL}?subject=${subject}`;
  }

  public onCheckAgain(): void {
    this.checkingByHand = true;
    this.onCheck().finally(() => (this.checkingByHand = false));
  }

  public async onCheck(): Promise<void> {
    if (!this.job || this.checking) {
      return;
    }
    window.clearTimeout(this.timer);
    this.checking = true;
    this.error = '';
    try {
      await this.flow.confirmPayment(this.job.modelId, this.sessionId || `stub_${this.job.modelId}`);
    } catch (e) {
      this.error = e instanceof RenderNotImplementedError ? e.message : 'The payment status could not be checked. We will keep trying.';
    } finally {
      this.checking = false;
      this.checks++;
      this.lastCheck = Date.now();
      // A confirmed payment moves the flow to the render, and this page goes.
      this.schedule(Date.now() - this.arrivedAt >= SLOW_AFTER_MS ? SLOW_CHECK_MS : QUICK_CHECK_MS);
    }
  }

  public onBack(): void {
    if (this.job) {
      this.flow.cancelCheckout(this.job.modelId);
    }
  }

  private schedule(delay: number): void {
    if (this.destroyed) {
      return;
    }
    window.clearTimeout(this.timer);
    // The first check waits for the job the URL names.
    this.timer = window.setTimeout(() => (this.job ? this.onCheck() : this.schedule(300)), delay);
  }
}
